import api from "../api";
import { on } from "../utils/listeners";
import destroyVueApps from "../utils/destroyVueApps";
import {
  updateThickPriceOnVariantChange,
  clearThickState
} from "./productPage";

const overrideCSRFToken = () => {
  const csrfTokenElement = document.querySelector("head meta[name=csrf-token]");
  const formMetaTags = document.querySelectorAll(
    "input[name=authenticity_token]"
  );

  if (csrfTokenElement) {
    Array.from(formMetaTags).forEach(tag => {
      tag.value = csrfTokenElement.content;
    });
  }
};

const refreshVariant = (target, { pushHistory = true } = {}) => {
  document
    .querySelectorAll(".loading-overlay")
    .forEach(el => el.classList.add("shown"));

  let url = target?.attributes?.href?.value || target?.value;
  if (!url) return;

  const full = new URL(url, window.location.origin);

  const params = new URL(document.location).searchParams;
  const version = params.get("version");
  if (version) full.searchParams.set("version", version);

  url = full.pathname + full.search;

  if (pushHistory) {
    window.history.pushState({ variantUrl: url }, "", window.countryCode + url);
  }

  // Retrieves the previous qty and thickness before we destroy the app
  const currentPicker = window.matchMedia("(min-width: 992px)").matches
    ? document.querySelector(".js-large-quantity-picker-desktop")
    : document.querySelector(".js-large-quantity-picker-mobile");
  const previouslySelectedQtyOption = currentPicker?.querySelector(
    ".js-listing-page-qty-option.selected"
  );
  const previousQty = previouslySelectedQtyOption?.dataset.qty;
  const wasThickSelected = document.querySelector(
    "#thickness-options input[type='radio'][value='double']"
  )?.checked;

  // Store which collapsible variants are expanded before swap
  const expandedVariants = [
    ...document.querySelectorAll(
      ".product-options:not(.hidden) .collapsible-variant.expanded"
    )
  ].map(el => el.closest("[data-attribute-name]")?.dataset.attributeName);

  // On mobile (AB 205 variant 2) the visible open state is driven by
  // `mobile-expanded`, not `expanded`. The swapped-in markup never carries
  // it, so track it separately and restore it below — otherwise the pill the
  // customer just interacted with snaps shut after the variant swap.
  const mobileExpandedVariants = new Set(
    [
      ...document.querySelectorAll(
        ".product-options:not(.hidden) .collapsible-variant.mobile-expanded"
      )
    ]
      .map(el => el.closest("[data-attribute-name]")?.dataset.attributeName)
      .filter(Boolean)
  );

  api.get(url).then(async response => {
    destroyVueApps();

    document.getElementById("product-page").outerHTML = response.data;

    // Restore expanded state on collapsible variants without transition
    expandedVariants.forEach(attributeName => {
      if (!attributeName) return;
      const variant = document.querySelector(
        `.product-options:not(.ab-test-control) [data-attribute-name="${attributeName}"] .collapsible-variant`
      );

      if (variant) {
        variant.classList.add("expanded", "no-transition");

        if (mobileExpandedVariants.has(attributeName)) {
          variant.classList.add("mobile-expanded");
        } else {
          // Collapsed mobile variants are clamped to their header's real
          // height (headers can wrap, so a fixed height would clip them)
          const header = variant.querySelector(".variant-header");
          const headerHeight = header?.offsetHeight;
          if (headerHeight > 0) {
            variant.style.setProperty(
              "--variant-header-height",
              `${headerHeight}px`
            );
          }
        }

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            variant.classList.remove("no-transition");
          });
        });
      }
    });

    window.initProductPage();
    window.initializeProductCarousel();
    window.addFavourites();
    window.trackProductView();

    const initializeProductVariantImports = (
      await import("./productVariantImports")
    ).default;

    initializeProductVariantImports();

    const htmlChanged = new Event("htmlChanged");
    window.dispatchEvent(htmlChanged);
    overrideCSRFToken();

    const thicknessContainer = document.querySelector("#thickness-options");
    const supportsThickNow = !!thicknessContainer?.querySelector(
      'input[name="thickness"][value="double"]'
    );

    // Sets the previous quantity and thickness selected for the pricing table
    if (previousQty) {
      const useThick = wasThickSelected && supportsThickNow;
      const selector = useThick
        ? `.js-listing-page-qty-option.thick-option[data-qty="${previousQty}"]`
        : `.js-listing-page-qty-option:not(.thick-option)[data-qty="${previousQty}"]`;
      const visiblePickerAfterSwap = window.matchMedia("(min-width: 992px)")
        .matches
        ? document.querySelector(".js-large-quantity-picker-desktop")
        : document.querySelector(".js-large-quantity-picker-mobile");
      visiblePickerAfterSwap?.querySelector(selector)?.click();
    }

    // Sets the previous thickness selected for the radio buttons
    if (wasThickSelected && supportsThickNow) {
      updateThickPriceOnVariantChange();
    } else if (wasThickSelected && !supportsThickNow) {
      clearThickState();
    }

    // Must set this up after thickness is handled
    window.setupPricingTable();

    // Reselect previous qty for the new thickness state in the pricing table
    if (previousQty) {
      const visiblePickerAfterSwap = window.matchMedia("(min-width: 992px)")
        .matches
        ? document.querySelector(".js-large-quantity-picker-desktop")
        : document.querySelector(".js-large-quantity-picker-mobile");
      const thickEnabledNow =
        visiblePickerAfterSwap?.classList.contains("thickness-enabled");
      const selector = thickEnabledNow
        ? `.js-listing-page-qty-option.thick-option[data-qty="${previousQty}"]`
        : `.js-listing-page-qty-option:not(.thick-option)[data-qty="${previousQty}"]`;
      visiblePickerAfterSwap?.querySelector(selector)?.click();
    }
  });
};

on("click", ".js-variant-option", el => {
  const closestAncestor = el.closest(".js-tracking-variant");

  analytics.track("Changed Variant", {
    type: closestAncestor.dataset.attributeName,
    analyticsId: el.dataset.analyticsId
  });
});

on("change", ".js-variant-options-select", el => {
  const closestAncestor = el.closest(".js-tracking-variant");
  const selectedOption = el.options[el.selectedIndex];
  if (!selectedOption) {
    return;
  }

  analytics.track("Changed Variant", {
    type: closestAncestor.dataset.attributeName,
    analyticsId: selectedOption.dataset.analyticsId
  });
});

on("click", ".js-variant-option", refreshVariant);
on("change", ".js-variant-options-select", refreshVariant);

// Listener to detect back/forward navigation and update variant switching from the pushState history
let boundPopstate = false;
const bindVariantPopstate = () => {
  if (boundPopstate) return;
  boundPopstate = true;

  window.addEventListener("popstate", e => {
    const variantUrl = e.state?.variantUrl;
    const fallbackUrl = window.location.pathname + window.location.search;

    refreshVariant(
      { value: variantUrl || fallbackUrl.replace(window.countryCode, "") },
      { pushHistory: false }
    );
  });
};
bindVariantPopstate();
